/**
 * Delivery Countdown Timer (REQ-011 P2-4)
 *
 * Displays stock status with countdown to fulfillment cutoff.
 * Now works with the combined trust-proof-points snippet.
 *
 * Business Rules:
 * - Fulfillment cutoff: 08:00 GMT+0
 * - Fulfillment days: Monday-Friday only
 * - Transit time: 2-5 business days (express shipping)
 * - Ships from: Lithuania
 *
 * Only applies to variants: trust-sticky, full-bundle
 */

(function () {
  'use strict';

  // Only run if the countdown element is actually visible.
  // CSS controls visibility based on data-variant (trust-sticky, full-bundle).
  // When no variant is set (e.g. direct preview without GrowthBook), the element
  // may still be visible, so we check computed display instead of the attribute.
  var countdownCheck = document.querySelector('[data-delivery-countdown]');
  if (!countdownCheck || window.getComputedStyle(countdownCheck).display === 'none') {
    return;
  }
  // Configuration
  var CUTOFF_HOUR_GMT = 8; // 08:00 GMT
  var TRANSIT_MIN_DAYS = 2;
  var TRANSIT_MAX_DAYS = 5;
  var UPDATE_INTERVAL_MS = 60000; // Update every minute

  // Elements - look in the new trust-proof-points container
  var containerEl = document.querySelector('[data-delivery-countdown]');
  var urgencyEl = document.querySelector('[data-delivery-urgency]');
  var dateEl = document.querySelector('[data-delivery-date]');

  if (!containerEl || !urgencyEl || !dateEl) {
    return;
  }

  /**
   * Get current time in GMT
   * @returns {Date}
   */
  function getNowGMT() {
    var now = new Date();
    return new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds()
    );
  }

  /**
   * Get today's cutoff time in GMT
   * @returns {Date}
   */
  function getTodayCutoffGMT() {
    var now = getNowGMT();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), CUTOFF_HOUR_GMT, 0, 0);
  }

  /**
   * Check if a date is a weekday (Mon-Fri)
   * @param {Date} date
   * @returns {boolean}
   */
  function isWeekday(date) {
    var day = date.getDay();
    return day >= 1 && day <= 5;
  }

  /**
   * Get next business day from a given date
   * @param {Date} date
   * @returns {Date}
   */
  function getNextBusinessDay(date) {
    var result = new Date(date);
    result.setDate(result.getDate() + 1);

    while (!isWeekday(result)) {
      result.setDate(result.getDate() + 1);
    }

    return result;
  }

  /**
   * Add business days to a date
   * @param {Date} startDate
   * @param {number} days
   * @returns {Date}
   */
  function addBusinessDays(startDate, days) {
    var result = new Date(startDate);
    var added = 0;

    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (isWeekday(result)) {
        added++;
      }
    }

    return result;
  }

  /**
   * Format date as "Jan 23"
   * @param {Date} date
   * @returns {string}
   */
  function formatShortDate(date) {
    var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[date.getMonth()] + ' ' + date.getDate();
  }

  /**
   * Calculate ship date and delivery range
   * @param {boolean} beforeCutoff
   * @returns {Object}
   */
  function calculateDates(beforeCutoff) {
    var now = getNowGMT();
    var shipDate;

    if (beforeCutoff && isWeekday(now)) {
      // Ships today
      shipDate = now;
    } else {
      // Ships next business day
      shipDate = getNextBusinessDay(now);
    }

    var transitMin = beforeCutoff ? TRANSIT_MIN_DAYS - 1 : TRANSIT_MIN_DAYS;
    var transitMax = beforeCutoff ? TRANSIT_MAX_DAYS - 1 : TRANSIT_MAX_DAYS;
    var deliveryMin = addBusinessDays(now, transitMin);
    var deliveryMax = addBusinessDays(now, transitMax);

    return {
      shipDate: shipDate,
      deliveryMin: deliveryMin,
      deliveryMax: deliveryMax
    };
  }

  /**
   * Format time remaining as "Xh Xm"
   * @param {number} ms Milliseconds remaining
   * @returns {string}
   */
  function formatTimeRemaining(ms) {
    if (ms <= 0) {
      return '0h 0m';
    }

    var totalMinutes = Math.floor(ms / 60000);
    var hours = Math.floor(totalMinutes / 60);
    var minutes = totalMinutes % 60;

    if (hours > 0) {
      return hours + 'h ' + minutes + 'm';
    }
    return minutes + 'm';
  }

  /**
   * Update the countdown display
   */
  function updateCountdown() {
    var now = getNowGMT();
    var cutoff = getTodayCutoffGMT();
    var beforeCutoff = now < cutoff && isWeekday(now);
    var msRemaining = cutoff - now;

    var dates = calculateDates(beforeCutoff);
    var deliveryRange;
    if (dates.deliveryMin.getMonth() === dates.deliveryMax.getMonth()) {
      // Same month: "Feb 25-28"
      deliveryRange = formatShortDate(dates.deliveryMin) + '-' + dates.deliveryMax.getDate();
    } else {
      // Cross-month: "Feb 25 - Mar 2"
      deliveryRange = formatShortDate(dates.deliveryMin) + ' - ' + formatShortDate(dates.deliveryMax);
    }

    if (beforeCutoff) {
      // Before cutoff: show countdown
      containerEl.classList.remove('ships-tomorrow');
      urgencyEl.innerHTML =
        'Order in <span class="trust-proof-points__timer" data-delivery-timer>' +
        formatTimeRemaining(msRemaining) +
        '</span>';
    } else {
      // After cutoff or weekend: ships next business day
      containerEl.classList.add('ships-tomorrow');
      var tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      var shipLabel;
      if (
        dates.shipDate.getFullYear() === tomorrow.getFullYear() &&
        dates.shipDate.getMonth() === tomorrow.getMonth() &&
        dates.shipDate.getDate() === tomorrow.getDate()
      ) {
        shipLabel = 'Ships tomorrow';
      } else {
        var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        shipLabel = 'Ships ' + dayNames[dates.shipDate.getDay()];
      }
      urgencyEl.textContent = shipLabel;
    }

    dateEl.textContent = 'Delivered ' + deliveryRange;
  }

  // Stock status elements
  var stockIconEl = document.querySelector('[data-stock-icon]');
  var stockLabelEl = document.querySelector('[data-stock-label]');
  var deliveryDetailsEl = document.querySelector('[data-delivery-details]');

  /**
   * Update trust proof point stock status based on variant availability
   * @param {boolean} available Whether the variant is available
   */
  function updateStockStatus(available) {
    if (stockIconEl) {
      stockIconEl.textContent = available ? '\u2713' : '\u2717';
      stockIconEl.classList.toggle('trust-proof-points__icon--green', available);
      stockIconEl.classList.toggle('trust-proof-points__icon--red', !available);
    }
    if (stockLabelEl) {
      stockLabelEl.textContent = available ? 'In Stock' : 'Out of Stock';
    }
    if (deliveryDetailsEl) {
      deliveryDetailsEl.style.display = available ? '' : 'none';
    }
    if (containerEl) {
      containerEl.setAttribute('data-variant-available', String(available));
    }
  }

  // Listen for variant changes from the theme's product form
  var productVariantsEl = document.querySelector('product-variants[data-main-product-variants]');
  if (productVariantsEl) {
    productVariantsEl.addEventListener('VARIANT_CHANGE', function (e) {
      var newVariant = e.target.currentVariant;
      var available = newVariant && newVariant.available;
      updateStockStatus(available);
    });
  }

  // Initial update
  updateCountdown();

  // Update every minute
  window.setInterval(updateCountdown, UPDATE_INTERVAL_MS);
})();
